
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import apiClient from './api/client';
import config from '../utils/config';

export interface BusinessConfig {
  id: string;
  name: string;
  description: string;
  rules: Record<string, any>;
  additional_context?: string;
  created_at: string;
  updated_at: string;
}

export interface BusinessConfigResponse {
  config: BusinessConfig;
}

class BusinessConfigService {
  private static instance: BusinessConfigService;
  private baseUrl: string;

  private constructor() {
    this.baseUrl = config.api.baseUrl;
  }

  public static getInstance(): BusinessConfigService {
    if (!BusinessConfigService.instance) {
      BusinessConfigService.instance = new BusinessConfigService();
    }
    return BusinessConfigService.instance;
  }

  async createConfig(config: Omit<BusinessConfig, 'workflow_id'>): Promise<BusinessConfig> {
    const response = await apiClient.post(`/api/business/config`, config);
    return response.data;
  }

  async getConfig(workflowId: string): Promise<BusinessConfig> {
    const response = await apiClient.get(`/api/business/config/${workflowId}`);
    return response.data;
  }

  async updateConfig(workflowId: string, config: Partial<BusinessConfig>): Promise<BusinessConfig> {
    const response = await apiClient.put(`/api/business/config/${workflowId}`, config);
    return response.data;
  }
}

// React Query hooks
export const useCreateBusinessConfig = () => {
  return useMutation({
    mutationFn: (config: Omit<BusinessConfig, 'workflow_id'>) =>
      BusinessConfigService.getInstance().createConfig(config),
  });
};

// Mock business config service
const mockBusinessConfig: BusinessConfig = {
  id: 'mock-config-1',
  name: 'Default Configuration',
  description: 'Default business rules configuration',
  rules: {
    threshold: 0.75,
    maxKeywords: 100,
    minConfidence: 0.5
  },
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
};

// Hook for fetching business configuration
export const useBusinessConfig = (workflowId: string) => {
  return useQuery({
    queryKey: ['businessConfig', workflowId],
    queryFn: async (): Promise<BusinessConfigResponse> => {
      console.log('Fetching business config for workflow:', workflowId);
      
      // Mock implementation - return default config
      return { config: mockBusinessConfig };
    },
    enabled: !!workflowId
  });
};

export const useUpdateBusinessConfig = () => {
  return useMutation({
    mutationFn: ({ workflowId, config }: { workflowId: string; config: Partial<BusinessConfig> }) =>
      BusinessConfigService.getInstance().updateConfig(workflowId, config),
  });
};

export default BusinessConfigService; 